import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  selectHeaderImagen,
  selectHeaderResponsiveImagen,
  selectTemplateImage,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { BillboardSkeleton } from './skeletons/BillboardSkeleton';

function selectLandingData(state) {
  return state.landing?.data ?? null;
}

const BillboardImage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);
  const imageLoadedRef = useRef(false);
  const imageRef = useRef(null);
  const location = useLocation();
  const landingData = useSelector(selectLandingData);
  const headerImagen = useSelector(selectHeaderImagen);
  const headerResponsiveImagen = useSelector(selectHeaderResponsiveImagen);
  const templateImage = useSelector(selectTemplateImage);

  const landingIdFromPath = useMemo(() => {
    const m = location.pathname.match(/^\/landing\/([^/]+)/);
    return m ? m[1] : null;
  }, [location.pathname]);

  const isLandingHero =
    landingIdFromPath != null &&
    landingData?.imagen &&
    Number(landingData.id) === Number(landingIdFromPath);

  const landingHeroSrc = isLandingHero ? landingData.imagen : null;
  const landingHeroMobileSrc =
    isLandingHero ? landingData.imagenMobile || null : null;

  const desktopSrc = landingHeroSrc || headerImagen || templateImage;
  const mobileSrc = landingHeroMobileSrc || (!landingHeroSrc ? headerResponsiveImagen : null);

  const hasMobileVariant = Boolean(mobileSrc);

  const handleImageLoad = useCallback(() => {
    if (!mountedRef.current) return;
    imageLoadedRef.current = true;
    setIsLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    if (!mountedRef.current) return;
    setIsLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    imageLoadedRef.current = false;
    if (desktopSrc) {
      setIsLoading(true);

      if (imageRef.current?.complete && imageRef.current?.naturalWidth > 0) {
        imageLoadedRef.current = true;
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [desktopSrc, mobileSrc]);

  return (
    <div
      className='w-full relative overflow-hidden h-[45vh] sm:h-[50vh] md:h-[50vh] lg:h-[42vh] min-h-[420px] md:min-h-[420px] lg:min-h-[380px]'
    >
      {isLoading && (
        <BillboardSkeleton />
      )}
      {hasMobileVariant ? (
        <picture className='block w-full h-full'>
          <source
            media='(max-width: 768px)'
            srcSet={mobileSrc}
          />
          <img
            alt='Billboard background'
            className={`w-full h-full object-cover object-center md:object-top ${isLoading ? 'hidden' : 'block'}`}
            onError={handleImageError}
            onLoad={handleImageLoad}
            ref={imageRef}
            src={desktopSrc}
          />
        </picture>
      ) : (
        <img
          alt='Billboard background'
          className={`w-full h-full object-cover object-center md:object-top ${isLoading ? 'hidden' : 'block'}`}
          onError={handleImageError}
          onLoad={handleImageLoad}
          ref={imageRef}
          src={desktopSrc}
        />
      )}
    </div>
  );
};

export default BillboardImage;
